import { useRouter } from "next/router";
import { FC, useEffect } from "react";

import { COOKIES, MonthInSeconds } from "constants/enum";
import { setCookie } from "utils/cookie";

/**
 * Ad-click identifiers we capture from the landing-page URL and persist so they
 * survive the redirect to the third-party Typeform quiz and the return into the
 * booking funnel. Add a new platform by appending one entry here. Param names are
 * case-sensitive and match each network's spec (e.g. Snapchat uses `ScCid`).
 *
 * `tsCookie` records when the click id was captured, for getCurrentExternalIdSource
 * to find the most recent one - omit it for a platform not in that resolver's
 * candidate list (e.g. Spotify), which just skips the timestamp write below.
 */
const AD_CLICK_IDS: ReadonlyArray<{
    param: string;
    cookie: COOKIES;
    tsCookie?: COOKIES;
}> = [
    {
        param: "rdt_cid",
        cookie: COOKIES.REDDIT_CLICK_ID,
        tsCookie: COOKIES.REDDIT_CLICK_ID_TS,
    },
    {
        param: "ttclid",
        cookie: COOKIES.TIKTOK_CLICK_ID,
        tsCookie: COOKIES.TIKTOK_CLICK_ID_TS,
    },
    {
        param: "ScCid",
        cookie: COOKIES.SNAPCHAT_CLICK_ID,
        tsCookie: COOKIES.SNAPCHAT_CLICK_ID_TS,
    },
    { param: "spclid", cookie: COOKIES.SPOTIFY_CLICK_ID },
    { param: "msclkid", cookie: COOKIES.MICROSOFT_CLICK_ID },
    { param: "oppref", cookie: COOKIES.CHATGPT_CLICK_ID },
    { param: "twclid", cookie: COOKIES.TWITTER_CLICK_ID },
];

/**
 * Captures ad-click IDs (Reddit `rdt_cid`, TikTok `ttclid`, extensible) from the
 * URL query and stores each, with its capture time, in its own cookie. Persisting
 * on the landing page (our domain) means the value is still available when the
 * user returns from the quiz into the booking flow. Supersedes the TikTok-only
 * TiktokService.
 */
export const AdClickIdService: FC<{}> = () => {
    const { query } = useRouter();

    useEffect(() => {
        AD_CLICK_IDS.forEach(({ param, cookie, tsCookie }) => {
            const value = query[param];

            if (value) {
                const cookieOptions = { path: "/", "max-age": MonthInSeconds };

                setCookie(cookie, String(value), cookieOptions);

                if (tsCookie) {
                    setCookie(tsCookie, String(Date.now()), cookieOptions);
                }
            }
        });
    }, [query]);

    return null;
};
